import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { observer } from "mobx-react";
import ReportsRouting from "Pages/Dashboard/reports_v2/ReportsRouting";
import TabNavigation from "Components/common/TabNavigation";
import FilterByDateTab from "Components/common/FilterByDateTab";
import UserDropdown from "Components/common/UserDropdown";
import RadioBtnComponent from "Components/common/radioBtn";
import { ReactComponent as LinkedInIcon } from "Assets/svg/linkedInOutlined.svg";
import { ReactComponent as DownArrow } from "Assets/svg/donw-arrow.svg";
import { REPORTS_NAVIGATION } from "Static/constant";
import userDetail from "Stores/userDetail";
import DateUtils from "Utils/DateUtils";
import { USER_SELECT_DROPDOWN_LIST as userObjList } from "Static/constant";
import Utils from "Utils/utils";
import reportsStore from "Pages/Dashboard/reports_v2/reportsStore";

// Small circular avatar for a LinkedIn account row - falls back to the account's initials (colored
// circle) when there's no profilePictureUrl, or the picture fails to load (broken/expired LinkedIn CDN
// links are common - LinkedIn signs these URLs with a TTL).
const LiAccountAvatar = ({ firstName = "", lastName = "", email = "", profilePictureUrl = "" }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const initials =
    `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase() ||
    (email?.charAt(0) || "?").toUpperCase();

  if (profilePictureUrl && !imgFailed) {
    return (
      <img
        className="liAccountRowAvatar"
        src={profilePictureUrl}
        alt=""
        onError={() => setImgFailed(true)}
      />
    );
  }
  return <span className="liAccountRowAvatar liAccountRowAvatarFallback">{initials}</span>;
};

// Single-select dropdown for the LinkedIn account filter, built to match the look/behaviour of the
// adjacent member dropdown (UserDropdown) exactly - same .userFilterBtn button, same %userListDropdown
// panel styling and RadioBtnComponent used there, instead of a bare, unstyled native <select>. Each row's
// `value` is a JSX fragment (avatar + name) rather than a plain string - RadioBtnComponent just renders
// whatever it's given inside the row's <label>, so this needs no changes to that shared component.
const LiAccountDropdown = ({ options = [], selectedId = "", onSelect = () => {} }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapRef = useRef(null);
  const hasNoAccounts = options.length === 0;

  const selectedAccount = options.find(account => account?.id === selectedId);
  const selectedLabel = selectedAccount
    ? `${selectedAccount.firstName || ""} ${selectedAccount.lastName || ""}`.trim() ||
      selectedAccount.email ||
      "--"
    : hasNoAccounts
    ? "No LinkedIn accounts"
    : "All LinkedIn accounts";

  const filteredOptions = searchTerm.trim()
    ? options.filter(account => {
        const { firstName = "", lastName = "", email = "" } = account || {};
        const haystack = `${firstName} ${lastName} ${email}`.toLowerCase();
        return haystack.includes(searchTerm.trim().toLowerCase());
      })
    : options;

  const handleSelect = (e, id) => {
    setOpen(false);
    setSearchTerm("");
    onSelect(id === "all" ? "" : id);
  };

  return (
    <div className="userFilterDropDown" ref={wrapRef}>
      <div
        className={`userFilterBtn ${hasNoAccounts ? "disabledBtn" : ""}`}
        onClick={() => !hasNoAccounts && setOpen(!open)}
      >
        <span className="icon">
          <LinkedInIcon />
        </span>
        <span className="name">{selectedLabel}</span>
        <span className={`arrow ${open ? "activeArrow" : ""}`}>
          <DownArrow />
        </span>
      </div>
      {open && !hasNoAccounts && (
        <div className="dropdownSection">
          <div className="dropdownContent">
            {options.length > 5 && (
              <input
                type="text"
                className="liAccountSearchInput"
                placeholder="Search accounts…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onClick={e => e.stopPropagation()}
                autoFocus
              />
            )}
            <div className="singleSelectSection liAccountListScroll">
              {!searchTerm && (
                <RadioBtnComponent
                  name="selectLiAccountRadioBtn"
                  id="all"
                  value="All LinkedIn accounts"
                  defaultChecked={!selectedId}
                  radioOnchange={handleSelect}
                />
              )}
              {filteredOptions.map(account => {
                const { id = "", firstName = "", lastName = "", email = "", profilePictureUrl = "" } = account || {};
                const displayName = `${firstName} ${lastName}`.trim() || email || "--";
                return (
                  <RadioBtnComponent
                    key={id}
                    name="selectLiAccountRadioBtn"
                    id={id}
                    value={
                      <span className="liAccountRow">
                        <LiAccountAvatar
                          firstName={firstName}
                          lastName={lastName}
                          email={email}
                          profilePictureUrl={profilePictureUrl}
                        />
                        <span className="liAccountRowName">{displayName}</span>
                      </span>
                    }
                    defaultChecked={selectedId === id}
                    radioOnchange={handleSelect}
                  />
                );
              })}
              {searchTerm && !filteredOptions.length && (
                <p className="liAccountSearchEmpty">No accounts match "{searchTerm}"</p>
              )}
            </div>
          </div>
        </div>
      )}
      {open && (
        <div className="overlayUserFilterMask" onClick={() => setOpen(false)} />
      )}
    </div>
  );
};

const ReportsBody = observer(() => {
  const {
    team = false,
    hasTeamMember = false,
    isTeamOwner = false,
    memberId = "",
  } = userDetail?.userInfo || {};
  const { noOfContactsAddedInTeam = false, newReportsEnabled = false } =
    userDetail?.userFeatureFlag || {};
  const [teamList, setTeamList] = useState("");
  const [teamLoader, setTeamLoader] = useState(true);
  const location = useLocation();

  // SG-2243 follow-up - LinkedIn Account filter only makes sense on the LinkedIn tab, and only for
  // reps who have (or had) 2+ distinct accounts. Reuses the same account lists and admin/non-admin
  // split already built for the inbox filter (see outbox/main.js's getLiAccountOptions).
  const isLinkedInTab = location?.pathname?.endsWith("/linkedin");
  const isAdmin = Utils.isAdmin();
  // Team-wide list (admin only) or the caller's own accounts (non-admin) - the full, unfiltered pool.
  const allLiAccountOptions = (
    isAdmin ? userDetail?.teamLinkedInAccounts : userDetail?.activeLinkedInAccounts
  ) || [];
  // reportsStore.memberIds is [] when "All"/team is selected in the UserDropdown, and one-or-more
  // memberIds when specific member(s) ("You" included) are selected. Narrow the LI account list to
  // just the selected member(s)' accounts in that case; team-wide selection keeps the full pool.
  const selectedMemberIds = reportsStore?.memberIds || [];
  const liAccountOptions =
    selectedMemberIds.length > 0
      ? allLiAccountOptions.filter(account => selectedMemberIds.includes(account?.memberId))
      : allLiAccountOptions;
  const hasMultiLiAccounts =
    allLiAccountOptions.length > 1 || !!userDetail?.hasMultiLiAccountHistory;
  const showLiAccountFilter = isLinkedInTab && hasMultiLiAccounts;

  const selectedLiAccountCbk = (value = "") => {
    reportsStore.setLiAccountIds(value ? [value] : []);
  };

  // If the member filter changes (e.g. switching from "All" to a specific rep, or between reps) and
  // the currently-selected LinkedIn account no longer belongs to the newly-selected member(s), clear
  // the stale selection instead of silently filtering on an account that's not even shown anymore.
  useEffect(() => {
    const selectedLiAccountId = reportsStore?.liAccountIds?.[0];
    if (
      selectedLiAccountId &&
      !liAccountOptions.some(account => account?.id === selectedLiAccountId)
    ) {
      reportsStore.setLiAccountIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMemberIds.join(","), liAccountOptions.length]);

  const getFilterByDate = async (value = {}) => {
    const { filterFromDate = "", filterToDate = "" } = value || {};
    let fromDate = DateUtils.getTimeStamp(filterFromDate, "start") || "";
    let toDate = DateUtils.getTimeStamp(filterToDate, "end") || "";
    let dateFilter = { fromDate, toDate };
    reportsStore.setSortType("ASC");
    reportsStore.setSortParam("contactsAddedCount");
    reportsStore.setDateFilter(dateFilter);
    reportsStore.setPageLimit(10);
    reportsStore.setPageNo(1);
    reportsStore.setLoading(true);
    await reportsStore.fetchReportsData({}, reportsStore?.tabType);
    reportsStore.setLoading(false);
  };

  const getTeamList = async () => {
    setTeamLoader(true);
    const resData = await Utils.userTeamList();
    userDetail.setUserInfo({
      ...userDetail?.userInfo,
      hasTeamMember: resData?.length ? resData : false,
      teamList: resData,
    });
    setTeamList(resData);
    setTeamLoader(false);
  };

  const selectedUserCbk = (value = "") => {
    if (value) {
      let tempArr = value || "";
      if (typeof value === "string") {
        if (value?.toLowerCase() === "you") {
          tempArr = new Array(memberId);
        } else if (value?.toLowerCase() === "all") {
          tempArr = [];
        }
      }
      reportsStore.setSortType("ASC");
      reportsStore.setSortParam("contactsAddedCount");
      reportsStore.setMemberIds(tempArr);
      reportsStore.setPageNo(1);
      reportsStore.setPageLimit(10);
    }
  };

  useEffect(() => {
    getTeamList();
    reportsStore.setActiveDateFilter("month");
    return () => {
      reportsStore.setDateFilter(DateUtils.getMonthTimeStamp());
      reportsStore.setActiveDateFilter("month");
      reportsStore.resetReports();
    };
  }, []);

  return (
    <div className="reportsBody">

      <div className="reportsHeaderCont">
        <TabNavigation tabList={REPORTS_NAVIGATION} activeTab={"activity"} />
        <div className="rightCont">
          <div className="filterDropdown">
            <UserDropdown
              team={team}
              teamList={teamList}
              teamLoader={teamLoader}
              selectedUserCbk={selectedUserCbk}
              isContactsAdded={noOfContactsAddedInTeam > 0}
              userFilterList={Utils.filterByUserTeamFilterList(userObjList)}
            />
          </div>
          {showLiAccountFilter && (
            <div className="filterDropdown liAccountFilterDropdown">
              <LiAccountDropdown
                options={liAccountOptions}
                selectedId={reportsStore?.liAccountIds?.[0] || ""}
                onSelect={selectedLiAccountCbk}
              />
            </div>
          )}
          <FilterByDateTab
            activeDueTab={reportsStore?.activeDateFilter}
            setActiveDueTab={reportsStore.setActiveDateFilter}
            cbk={getFilterByDate}
            isTaskPage={false}
            pageType="reports"
          />
        </div>
      </div>
      <ReportsRouting />
    </div>
  );
});

export default ReportsBody;
export { ReportsBody };
